// components/Game.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRadixConnect } from '../radix_connect';
import { useAuth, useGameData } from '../backend';
import CyberpunkOffice from './Office/CyberpunkOffice';
import { formatTokenAmount } from '../utils/formatTokenAmount';
// Removed imports for deleted components
import '../styles/Game.scss';

// Income Notification Component
const IncomeNotification = ({ income, rewardTokenSymbol, onClose }) => {
  useEffect(() => {
    // Auto-close after 5 seconds
    const timer = setTimeout(() => {
      onClose();
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [onClose]);
  
  return (
    <div className="income-notification">
      <div className="notification-content">
        <div className="notification-header">
          <h3>Passive Income</h3>
          <button className="close-btn" onClick={onClose}>x</button>
        </div>
        <div className="notification-body">
          <p>Your mining rigs generated:</p>
          <p className="income-amount">+{formatTokenAmount(income)} {rewardTokenSymbol}</p>
          <p className="income-note">while you were away!</p>
        </div>
      </div>
    </div>
  );
};

// Money Display Component that updates independently from the main game
const MoneyDisplay = React.memo(({ money, rewardTokenSymbol }) => {
  return (
    <div className="money-display">
      <span className="money-value">{formatTokenAmount(money)} {rewardTokenSymbol}</span>
    </div>
  );
});

MoneyDisplay.displayName = 'MoneyDisplay';

// Error boundary component to catch rendering errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', color: 'white', background: 'rgba(0,0,0,0.8)' }}>
          <h2>Something went wrong.</h2>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            <summary>Error Details</summary>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}

const Game = () => {
  const { saveId } = useParams();
  const navigate = useNavigate();
  const startRoute = '/start';
  const { rdt, connected, accounts, selectedAccount } = useRadixConnect();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showIncomeNotification, setShowIncomeNotification] = useState(false);
  const [incomeAmount, setIncomeAmount] = useState(0);
  const [currentMoney, setCurrentMoney] = useState(0);
  const [moneyDisplayKey, setMoneyDisplayKey] = useState(Date.now());

  console.log("Game component rendering with saveId:", saveId);

  // Derive wallet state from accounts
  const wallet = useMemo(() => ({
    rdt,
    connected: connected || Boolean(selectedAccount?.address) || (accounts && accounts.length > 0),
    address: selectedAccount?.address || (accounts && accounts.length > 0 ? accounts[0].address : null),
    accounts
  }), [rdt, connected, selectedAccount, accounts]);

  console.log("Wallet state:", wallet);

  // Use auth and game data hooks
  const { 
    userId, 
    isAuthenticated, 
    isLoading: authLoading,
    sessionToken,
  } = useAuth(wallet);
  
  console.log("Auth state:", { userId, isAuthenticated, authLoading });
  
  const { 
    gameSave, 
    isLoading: gameLoading, 
    loadSave,
    lastIncomeUpdate 
  } = useGameData(userId, sessionToken);

  console.log("Game data state:", { 
    hasGameSave: !!gameSave, 
    gameSaveId: gameSave?.save_id,
    gameLoading, 
    hasLastIncomeUpdate: !!lastIncomeUpdate 
  });

  const rewardTokenSymbol = gameSave?.constants?.REWARD_TOKEN_SYMBOL || 'DCKSLAP';
  const silentReloadGameData = useCallback(() => {
    return loadSave({ silent: true });
  }, [loadSave]);

  // Force refresh of money display
  const refreshMoneyDisplay = useCallback((nextMoney = null) => {
    const balanceToDisplay =
      typeof nextMoney === 'number'
        ? nextMoney
        : (gameSave && typeof gameSave.money === 'number' ? gameSave.money : null);

    if (typeof balanceToDisplay === 'number') {
      setCurrentMoney(balanceToDisplay);
      setMoneyDisplayKey(Date.now());
    }
  }, [gameSave]);

  // Update current money when game data changes
  useEffect(() => {
    if (gameSave && typeof gameSave.money === 'number') {
      setCurrentMoney(gameSave.money);
    }
  }, [gameSave]);

  // Check wallet connection and authentication
  useEffect(() => {
    console.log("Checking wallet and auth:", { connected: wallet.connected, isAuthenticated, authLoading });
    
    // Only check after auth loading is complete
    if (authLoading) {
      console.log("Auth still loading, not checking yet");
      return;
    }
    
    if (!isAuthenticated) {
      console.error('Not authenticated, redirecting to start');
      setError('Not authenticated');
      navigate(startRoute);
      return;
    }
  }, [wallet.connected, isAuthenticated, navigate, authLoading, startRoute]);

  // Check if we have the correct save
  useEffect(() => {
    console.log("Checking save:", { authLoading, isAuthenticated, userId, gameLoading, gameSave, saveId });
    
    if (authLoading) {
      console.log("Auth still loading, not checking save yet");
      return;
    }

    if (!isAuthenticated || !userId) {
      console.log("Auth not ready for save validation yet");
      return;
    }
    
    // Only check after game data loading is complete
    if (gameLoading) {
      console.log("Game data still loading, not checking save yet");
      return;
    }
    
    if (!gameSave) {
      console.error('No game save found, redirecting to start');
      setError('Game save not found');
      navigate(startRoute);
      return;
    }
    
    if (gameSave.save_id !== saveId) {
      console.error('Invalid game save, redirecting to start');
      setError('Invalid game save');
      navigate(startRoute);
    }
  }, [authLoading, isAuthenticated, userId, gameLoading, gameSave, saveId, navigate, startRoute]);

  // Show income notification if we received passive income
  useEffect(() => {
    if (lastIncomeUpdate?.income > 0) {
      setIncomeAmount(lastIncomeUpdate.income);
      setShowIncomeNotification(true);
    }
  }, [lastIncomeUpdate]);

  // Handle scene loaded callback - directly set loading to false
  const handleSceneLoaded = useCallback(() => {
    console.log("Scene loaded callback triggered, setting loading to false");
    setLoading(false);
    // Immediately update loading state if we have game data
    // This check is less critical now but doesn't hurt
    if (gameSave) {
      console.log("Game data available, ensuring loading is false");
    }
  }, [gameSave]); // Added gameSave dependency for safety, though setLoading doesn't directly use it

  // If we have game data but no loading state has been set yet, show loading
  // This effect now primarily acts as a failsafe timeout
  useEffect(() => {
    console.log("Loading effect triggered:", { gameSave: !!gameSave, loading });

    if (gameSave && loading) {
      // Set a timer to ensure we don't get stuck in loading
      const timer = setTimeout(() => {
        if (loading) { // Check again in case handleSceneLoaded was faster
          console.log("Forcing loading to false after timeout");
          setLoading(false);
        }
      }, 8000); // Increased timeout slightly

      return () => clearTimeout(timer);
    }
    // Removed sceneLoaded dependency
  }, [gameSave, loading]);

  // Handle updating game data in Supabase
  const handleUpdateGameData = useCallback(async (updatedData) => {
    if (!updatedData) {
      return;
    }

    if (typeof updatedData.money === 'number') {
      refreshMoneyDisplay(updatedData.money);
    }

    await silentReloadGameData();
  }, [refreshMoneyDisplay, silentReloadGameData]);

  // Close income notification
  const handleCloseIncomeNotification = () => {
    setShowIncomeNotification(false);
  };

  // Loading states
  if (authLoading || gameLoading) {
    return (
      <div className="game-container">
        <div className="game-loading">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading Game Data...</div>
        </div>
      </div>
    );
  }

  // This condition should now cover both waiting for game data AND scene loading
  if (loading || !gameSave) {
    // Display error if gameSave is missing after loading checks
    if (!gameSave && !gameLoading && !authLoading) {
       return (
         <div className="game-container">
           <div className="game-loading">
             <div className="loading-text">Error loading game data</div>
             <div className="loading-subtext">{error || "Game save not found"}</div>
           </div>
         </div>
       );
    }
    // Otherwise, show the main loading screen
    return (
      <div className="game-container">
        <div className="game-loading">
          <div className="loading-spinner"></div>
          {/* Updated loading text */}
          <div className="loading-text">Loading Cyberpunk Office...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-container">
      <MoneyDisplay
        key={moneyDisplayKey}
        money={currentMoney}
        rewardTokenSymbol={rewardTokenSymbol}
      />
      
      <div className="game-content">
        <ErrorBoundary>
          <CyberpunkOffice 
            onSceneLoaded={handleSceneLoaded}
            gameData={gameSave}
            updateGameData={handleUpdateGameData}
            reloadGameData={silentReloadGameData}
            refreshMoneyDisplay={refreshMoneyDisplay}
          />
        </ErrorBoundary>
      </div>
      
      {showIncomeNotification && (
        <IncomeNotification 
          income={incomeAmount} 
          rewardTokenSymbol={rewardTokenSymbol}
          onClose={handleCloseIncomeNotification} 
        />
      )}
    </div>
  );
};

export default Game;
